import { memo, useEffect, useRef, useState } from 'react';

import { IconFormIndicatorWarning } from '@components/icons/form-indicator-warning';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconTrash } from '@components/icons/trash';
import { Menu } from '@components/menu';
import { useScopedTranslate } from '@locale/translate';

import { JourneyAction } from 'api/types';
import { ACTION_TYPES, getActionLabel } from 'ui/actions/action-config';
import { JourneyNodeActionCard, MoreItems } from 'ui/journey-builder/node-types/node-container';
import { NodeContainer } from 'ui/journey-builder/node-types/node-container';
import { JourneyNode } from 'ui/journey-builder/node-types/types';

import { getConditionDescription, getConditionTooltip } from '../condition/journey-node';

const ACTION_TYPE = ACTION_TYPES.break_loop;

export const getDescription = (node: JourneyAction<typeof ACTION_TYPE>): string => {
  return getConditionDescription({
    values: node.values,
    styled: false,
    locale: descriptionLocale,
  });
};

export const Node = memo((node: JourneyNode<typeof ACTION_TYPE>) => {
  const { t } = useScopedTranslate(locale);

  const values = node.data?.values;

  const textClampedRef = useRef<HTMLDivElement | null>(null);
  const [isTextClamped, setIsTextClamped] = useState(false);
  const descriptionText = getConditionDescription({
    values,
    textClampedRef,
    styled: true,
    locale: descriptionLocale,
  });

  useEffect(() => {
    const descriptionChildren = [...(descriptionText?.ref?.current?.children ?? [])];
    const isDescriptionTextClamped = descriptionChildren.some((child) => {
      return child.scrollHeight > child.clientHeight;
    });

    setIsTextClamped(isDescriptionTextClamped);
  }, [descriptionText]);

  return (
    <NodeContainer node={node}>
      <JourneyNodeActionCard
        node={node}
        label={getActionLabel(ACTION_TYPE)}
        descriptionText={descriptionText}
        tooltip={getConditionTooltip({ values, isTextClamped, locale: descriptionLocale })}
        renderBelowDescription={<MoreItems items={values?.conditions} />}
      >
        {node.data?.isConfigured ? <></> : <IconFormIndicatorWarning />}
        <Menu buttonProps={{ variant: 'ghost', iconRight: <IconMoreVertical /> }}>
          <Menu.Item
            label={t('core.delete')}
            isDisabled
            variant="danger"
            icon={<IconTrash />}
            tooltip={t('cantRemove')}
          />
        </Menu>
      </JourneyNodeActionCard>
    </NodeContainer>
  );
});

const locale = {
  en: {
    cantRemove: "Can't delete Exit loop actions. Delete the loop action to delete both",
  },
};

export const descriptionLocale = {
  en: {
    configureAction: 'Configure action',
    continueIf: 'Exit loop if\n',
    continueIfNoStyle: 'Exit loop if\n{conditions}',
    conditionDescription: '{conjunction}{field} {comparator} {value}',
    conditionDescriptionNoStyle: '{field} {comparator} {value}',
    is: 'is',
    isNot: 'is not',
    booleanTrue: 'true',
    booleanFalse: 'false',
    before: 'Before',
    isOrBefore: 'Is or before',
    isOrAfter: 'Is or after',
    after: 'After',
    and: 'AND',
    or: 'OR',
    contains: 'contains',
    notContain: 'does not contain',
    equalTo: 'equal to',
    notEqualTo: 'not equal to',
    empty: 'is empty',
    notEmpty: 'is not empty',
    anyOf: 'is any of',
    noneOf: 'is none of',
    moreThan: 'more than',
    moreOrEqualTo: 'more or equal to',
    lessThan: 'less than',
    lessOrEqualTo: 'less or equal to',
    startsWith: 'starts with',
    endsWith: 'ends with',
    changesValue: 'changes value',
  },
};
